import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
    Users, Coins, ArrowUpRight, ArrowDownRight, Search, Plus, Minus, RefreshCw,
    Settings, Shield, Ban, AlertTriangle, CheckCircle, XCircle, Star, Gift, MessageCircle, Volume2, VolumeX, Bell, BellOff,
    TrendingUp, DollarSign, FileText, Clock, Activity, Trash2, ToggleLeft, ToggleRight, Trophy, BarChart3, PieChart, KeyRound, Flame, Sparkles, Ticket, X, Loader2
} from 'lucide-react';
import axios from 'axios';
import setIfChanged from '../lib/setIfChanged';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
    LineChart, Line, BarChart, Bar, PieChart as RePieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Area, AreaChart
} from 'recharts';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { playSupportPing, isSupportSoundMuted, setSupportSoundMuted } from '../utils/supportPing';
import { useNewSignupAlerts } from '../hooks/useNewSignupAlerts';
import { MessagePlayerProvider } from '../contexts/MessagePlayerContext';
import { LoanOwedProvider } from '../contexts/LoanOwedContext';
import { SquareReceiptsProvider } from '../contexts/SquareReceiptsContext';
import { StaffAlertsProvider } from '../contexts/StaffAlertsContext';
import StaffAlertsBell from '../components/StaffAlertsBell';
import PlatformCreditsTile from '../components/PlatformCreditsTile';
import { PlayerDrilldownProvider } from '../components/PlayerDrilldownContext';
import HomeDashboard from './admin/HomeDashboard';
import SupportMessagePopup from '../components/admin/SupportMessagePopup';
import LiveCtClock from '../components/LiveCtClock';
import ForgotPasswordInbox from '../components/ForgotPasswordInbox';
import { useAdminShortcuts } from '../hooks/useAdminShortcuts';
import { setTabSignal, playChime as playCashoutChime } from '../lib/tabAlerts';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { FreePlayTab } from './admin/FreePlayTab';
import { PromoPerformanceTab } from './admin/PromoPerformanceTab';
import { UsersAdminTab } from './admin/UsersAdminTab';
import ContactsAdminPanel from './admin/ContactsAdminPanel';
import { CashoutsAdminTab } from './admin/CashoutsAdminTab';
import { PromoCodesAdminTab } from './admin/PromoCodesAdminTab';
import { DailySpecialAdminPanel } from './admin/DailySpecialAdminPanel';
import { LuckyPackAdminPanel } from './admin/LuckyPackAdminPanel';
import { SeriesPackAdminPanel } from './admin/SeriesPackAdminPanel';
import { KioskAdminPanel } from './admin/KioskAdminPanel';
import { BonusFeedAdminPanel } from './admin/BonusFeedAdminPanel';
import { AmbassadorAdminPanel } from './admin/AmbassadorAdminPanel';
import { WheelsAdminPanel } from './admin/WheelsAdminPanel';
import { MajikReconciliationPanel } from './admin/MajikReconciliationPanel';
import { MajikErrorsPanel } from './admin/MajikErrorsPanel';
import { AuditAdminTab } from './admin/AuditAdminTab';
import { TransactionsAdminTab } from './admin/TransactionsAdminTab';
import { PayPalLoadsAdminTab } from './admin/PayPalLoadsAdminTab';
import { CashbackAdminTab } from './admin/CashbackAdminTab';
import { ReferralsAdminTab } from './admin/ReferralsAdminTab';
import { HierarchyAdminTab } from './admin/HierarchyAdminTab';
import { AnalyticsChartsAdminTab } from './admin/AnalyticsChartsAdminTab';
import { SettingsAdminTab } from './admin/SettingsAdminTab';
import SupportHoursAdminPanel from './admin/SupportHoursAdminPanel';
import PokerHappyHourPanel from './admin/PokerHappyHourPanel';
import PokerBuyInBoostPanel from './admin/PokerBuyInBoostPanel';
import PokerAdminPanel from './admin/PokerAdminPanel';
import { SinceLastLoginDigest } from './admin/SinceLastLoginDigest';
import { JackpotsAdminTab } from './admin/JackpotsAdminTab';
import { BofJackpotsAdminTab } from './admin/BofJackpotsAdminTab';
import { BofReportsAdminTab } from './admin/BofReportsAdminTab';
import { BuffaloBucksReportsAdminTab } from './admin/BuffaloBucksReportsAdminTab';
import { BofSettingsAdminTab } from './admin/BofSettingsAdminTab';
import { ThunderCashReportsAdminTab } from './admin/ThunderCashReportsAdminTab';
import { ThunderCashSettingsAdminTab } from './admin/ThunderCashSettingsAdminTab';
import { PandaParadiseSettingsAdminTab } from './admin/PandaParadiseSettingsAdminTab';
import { OutlawsLootReportsAdminTab } from './admin/OutlawsLootReportsAdminTab';
import { CasinoJackpotConfigPanel } from './admin/CasinoJackpotConfigPanel';
import { ThreeCardPokerConfigPanel } from './admin/ThreeCardPokerConfigPanel';
import { CrapsConfigPanel } from './admin/CrapsConfigPanel';
import { RouletteConfigPanel } from './admin/RouletteConfigPanel';
import { GameAvailabilityPanel } from './admin/GameAvailabilityPanel';
import { MalfunctionGuardPanel } from './admin/MalfunctionGuardPanel';
import { SportsRiskPanel } from './admin/SportsRiskPanel';
import SportsAdminTab from './admin/SportsAdminTab';
import BlackjackReportCard from '../components/admin/BlackjackReportCard';
import SportsbookReportCard from '../components/admin/SportsbookReportCard';
import ThreeCardPokerReportCard from '../components/admin/ThreeCardPokerReportCard';
import CrapsReportCard from '../components/admin/CrapsReportCard';
import RouletteReportCard from '../components/admin/RouletteReportCard';
import CasinoSyncAdminTab from './admin/CasinoSyncAdminTab';
import HouseRakeTile from './admin/HouseRakeTile';
import StreetDiceSimPanel from './admin/StreetDiceSimPanel';
import DiceSettingsPanel from './admin/dice/DiceSettingsPanel';
import TonkSettingsPanel from './admin/tonk/TonkSettingsPanel';
import TonkTablesPanel from './admin/tonk/TonkTablesPanel';
import TonkAuditPanel from './admin/tonk/TonkAuditPanel';
import { LotteryAdminTab } from './admin/LotteryAdminTab';
import { BulkDrawBackfillPanel } from './admin/BulkDrawBackfillPanel';
import { AdminLotteryHelperPanel } from './admin/AdminLotteryHelperPanel';
import AdminFlaggedTicketsTab from './admin/AdminFlaggedTicketsTab';
import { ReportsAdminTab } from './admin/ReportsAdminTab';
import GamesPerformancePanel from './admin/GamesPerformancePanel';
import PaybackReportPanel, { PaybackAlertStrip } from './admin/PaybackReportPanel';
import DeepSeaFurtuneAdminTab from './admin/dsf/DeepSeaFurtuneAdminTab';
import DailyProfitPanel from './admin/DailyProfitPanel';
import { CashoutSettingsPanel } from './admin/CashoutSettingsPanel';
import { PlayerPnLTab } from './admin/PlayerPnLTab';
import BonusAnalyticsPanel from './admin/BonusAnalyticsPanel';
import { DangerZone } from './admin/DangerZone';
import { StaffAdminTab } from './admin/StaffAdminTab';
import { AdminCashiersTab } from './admin/AdminCashiersTab';
import HelpAdminTab from './admin/HelpAdminTab';
import RoleAccessAdminTab from './admin/RoleAccessAdminTab';
import { SupportAdminTab } from './admin/SupportAdminTab';
import { AnnouncementsAdminTab } from './admin/AnnouncementsAdminTab';
import { KenoConfigsAdminTab } from './admin/KenoConfigsAdminTab';
import KenoReportsAdminTab from './admin/KenoReportsAdminTab';
import KenoRoundLookupTab from './admin/KenoRoundLookupTab';
import FloorLiveAdminTab from './admin/FloorLiveAdminTab';
import { GamePlayersTab } from '../components/gamePlayers/GamePlayersTab';
import LuckyDuckyProfilesAdminTab from './admin/LuckyDuckyProfilesAdminTab';
import LuckyDuckyReportsAdminTab from './admin/LuckyDuckyReportsAdminTab';
import BotStudioAdminTab from './admin/BotStudioAdminTab';
import LuckyDuckyQAAdminTab from './admin/LuckyDuckyQAAdminTab';
import GameRtpAdminTab from './admin/GameRtpAdminTab';
import ActivePlayersNowWidget from '../components/admin/ActivePlayersNowWidget';
import CasinoAgentLowBanner from '../components/admin/CasinoAgentLowBanner';
import CreditBurnBanner from '../components/admin/CreditBurnBanner';
import CasinoIncomeWidget from '../components/admin/CasinoIncomeWidget';
import SystemHealthBoard from '../components/admin/SystemHealthBoard';
import { ADMIN_SECTIONS } from './admin/adminSections';
import WeeklySettlementPanel from './admin/WeeklySettlementPanel';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminDashboard = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [currentSection, setCurrentSection] = useState('players');
    const [showContacts, setShowContacts] = useState(false);
    const [currentTab, setCurrentTab] = useState('users');
    // Per-device Advanced toggle — owners who want everything visible can pin it on.
    const [showAdvanced, setShowAdvanced] = useState(() => {
        try { return localStorage.getItem('admin-advanced-tabs') === '1'; } catch { return false; }
    });
    useEffect(() => {
        try { localStorage.setItem('admin-advanced-tabs', showAdvanced ? '1' : '0'); } catch { /* ignore */ }
    }, [showAdvanced]);

    // Universal command palette — type any tab name to jump straight to it.
    const [paletteQuery, setPaletteQuery] = useState('');
    const [paletteOpen, setPaletteOpen] = useState(false);

    // Flat searchable index of every (section, tab, advanced) triple.
    const paletteIndex = useMemo(() =>
        ADMIN_SECTIONS.flatMap(s => [
            ...s.tabs.map(t => ({ section: s.id, sectionLabel: s.label, tab: t.value, label: t.label, icon: t.icon, advanced: false })),
            ...s.advancedTabs.map(t => ({ section: s.id, sectionLabel: s.label, tab: t.value, label: t.label, icon: t.icon, advanced: true })),
        ]),
        []);

    const paletteMatches = useMemo(() => {
        const q = paletteQuery.trim().toLowerCase();
        if (!q) return [];
        return paletteIndex.filter(r =>
            r.label.toLowerCase().includes(q) || r.sectionLabel.toLowerCase().includes(q),
        ).slice(0, 8);
    }, [paletteIndex, paletteQuery]);

    const jumpToTab = useCallback((row) => {
        setCurrentSection(row.section);
        setCurrentTab(row.tab);
        if (row.advanced) setShowAdvanced(true);
        setPaletteQuery('');
        setPaletteOpen(false);
    }, []);

    // Deep-link into the player directory with a preset ambassador filter —
    // fired by the revenue-summary tiles in the Ambassador panel.
    useEffect(() => {
        const handler = (e) => {
            try { sessionStorage.setItem('admin_players_amb_filter', e?.detail?.filter || ''); } catch { /* ignore */ }
            setCurrentSection('players');
            setCurrentTab('users');
        };
        window.addEventListener('admin:goto-players', handler);
        return () => window.removeEventListener('admin:goto-players', handler);
    }, []);
    const [users, setUsers] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [cashoutRequests, setCashoutRequests] = useState([]);
    const [settings, setSettings] = useState({});
    const [analytics, setAnalytics] = useState({});
    const [untaggedSummary, setUntaggedSummary] = useState({ loads_count: 0, loads_total: 0, cashouts_count: 0, cashouts_total: 0 });
    const [auditLogs, setAuditLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [creditAmount, setCreditAmount] = useState('');
    const [creditOperation, setCreditOperation] = useState('deposit');
    const [creditDialogOpen, setCreditDialogOpen] = useState(false);
    const [creditLoading, setCreditLoading] = useState(false);
    const [creditMethod, setCreditMethod] = useState('');
    const [creditIsComp, setCreditIsComp] = useState(false);
    const [creditIsLoan, setCreditIsLoan] = useState(false);
    const CREDIT_PAYMENT_METHODS = ['Cash', 'Cash App', 'Chime', 'PayPal', 'Venmo', 'Zelle', 'Apple Pay', 'Other'];
    const [statusDialogOpen, setStatusDialogOpen] = useState(false);
    const [freeSpinsDialogOpen, setFreeSpinsDialogOpen] = useState(false);
    const [freeSpinsAmount, setFreeSpinsAmount] = useState('10');
    const [settingsLoading, setSettingsLoading] = useState(false);
    const [promoCodes, setPromoCodes] = useState([]);
    const [promoRedemptions, setPromoRedemptions] = useState([]);
    const [cashbackStats, setCashbackStats] = useState({});
    const [promoDialogOpen, setPromoDialogOpen] = useState(false);
    const [promoLoading, setPromoLoading] = useState(false);
    const [cashbackProcessing, setCashbackProcessing] = useState(false);
    const [cashbackResult, setCashbackResult] = useState(null);
    const [newPromo, setNewPromo] = useState({
        code: '', bonus_type: 'credits', amount: 0, max_uses: 0,
        min_vip_tier: 0, min_deposit: 0, expires_at: '', description: ''
    });
    const [referralStats, setReferralStats] = useState({});
    const [hierarchyData, setHierarchyData] = useState([]);
    const [lotteryAlertCount, setLotteryAlertCount] = useState(0);
    const [supportUnreadCount, setSupportUnreadCount] = useState(0);
    const [majikReconGap, setMajikReconGap] = useState(0);
    const [soundMuted, setSoundMuted] = useState(isSupportSoundMuted());
    const [notifPermission, setNotifPermission] = useState(
        typeof Notification !== 'undefined' ? Notification.permission : 'default'
    );
    const supportLastSeenRef = useRef({ initialized: false, at: null, convoId: null });
    const [revenueChart, setRevenueChart] = useState([]);
    const [playerActivity, setPlayerActivity] = useState([]);
    const [gamePopularity, setGamePopularity] = useState([]);
    const [topPlayers, setTopPlayers] = useState({ top_winners: [], top_wagerers: [] });
    const [createDistOpen, setCreateDistOpen] = useState(false);
    const [distTransferOpen, setDistTransferOpen] = useState(false);
    const [selectedDist, setSelectedDist] = useState(null);
    const [distTransferAmount, setDistTransferAmount] = useState('');
    const [newDist, setNewDist] = useState({ email: '', password: '', name: '', credit_rate: 0.80 });
    const [distActionLoading, setDistActionLoading] = useState(false);
    const [resetPwDialogOpen, setResetPwDialogOpen] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [resetPwLoading, setResetPwLoading] = useState(false);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const [confirmActionBusy, setConfirmActionBusy] = useState(false);
    const [financialReport, setFinancialReport] = useState(null);
    const [allBetsReport, setAllBetsReport] = useState([]);
    const [reportsLoading, setReportsLoading] = useState(false);
    const [drawState, setDrawState] = useState('IL');
    const [drawPickType, setDrawPickType] = useState(3);
    const [drawNumbers, setDrawNumbers] = useState([null, null, null]);
    const [drawDate, setDrawDate] = useState(new Date().toISOString().split('T')[0]);
    const [drawTime, setDrawTime] = useState('evening');
    const [drawLoading, setDrawLoading] = useState(false);
    const [lotteryTickets, setLotteryTickets] = useState([]);
    const [lotteryDraws, setLotteryDraws] = useState([]);
    const [lotteryAdminSettings, setLotteryAdminSettings] = useState(null);
    const [editRules, setEditRules] = useState('');
    const [promoUserId, setPromoUserId] = useState('');
    const [promoSelectedUserIds, setPromoSelectedUserIds] = useState([]);
    const [promoUserSearch, setPromoUserSearch] = useState('');
    const [promoAmount, setPromoAmount] = useState('');
    const [promoReason, setPromoReason] = useState('');
    const [promoHistory, setPromoHistory] = useState([]);
    const [promoReport, setPromoReport] = useState(null);
    const [promoTemplates, setPromoTemplates] = useState([]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [usersRes, transRes, cashoutRes, settingsRes, analyticsRes, logsRes, promoRes, redemptionsRes, cbStatsRes, refStatsRes, hierarchyRes, revenueRes, activityRes, popularityRes, topPlayersRes, untaggedRes] = await Promise.all([
                axios.get(`${API}/admin/users`, { withCredentials: true }),
                axios.get(`${API}/admin/transactions?limit=100`, { withCredentials: true }),
                axios.get(`${API}/admin/cashout-requests`, { withCredentials: true }),
                axios.get(`${API}/admin/settings`, { withCredentials: true }),
                axios.get(`${API}/admin/analytics`, { withCredentials: true }),
                axios.get(`${API}/admin/audit-logs?limit=50`, { withCredentials: true }),
                axios.get(`${API}/admin/promo-codes`, { withCredentials: true }).catch(() => ({ data: [] })),
                axios.get(`${API}/admin/promo-redemptions`, { withCredentials: true }).catch(() => ({ data: [] })),
                axios.get(`${API}/admin/cashback-stats`, { withCredentials: true }).catch(() => ({ data: {} })),
                axios.get(`${API}/admin/referral-stats`, { withCredentials: true }).catch(() => ({ data: {} })),
                axios.get(`${API}/admin/hierarchy`, { withCredentials: true }).catch(() => ({ data: [] })),
                axios.get(`${API}/admin/analytics/revenue-chart?days=30`, { withCredentials: true }).catch(() => ({ data: [] })),
                axios.get(`${API}/admin/analytics/player-activity?days=30`, { withCredentials: true }).catch(() => ({ data: [] })),
                axios.get(`${API}/admin/analytics/game-popularity`, { withCredentials: true }).catch(() => ({ data: [] })),
                axios.get(`${API}/admin/analytics/top-players`, { withCredentials: true }).catch(() => ({ data: { top_winners: [], top_wagerers: [] } })),
                axios.get(`${API}/admin/untagged-summary?days=7`, { withCredentials: true }).catch(() => ({ data: { loads_count: 0, loads_total: 0, cashouts_count: 0, cashouts_total: 0 } })),
            ]);
            setUsers(usersRes.data);
            setTransactions(transRes.data);
            setCashoutRequests(cashoutRes.data);
            setSettings(settingsRes.data);
            setAnalytics(analyticsRes.data);
            setAuditLogs(logsRes.data);
            setPromoCodes(promoRes.data);
            setPromoRedemptions(redemptionsRes.data);
            setCashbackStats(cbStatsRes.data);
            setReferralStats(refStatsRes.data);
            setHierarchyData(hierarchyRes.data);
            setRevenueChart(revenueRes.data);
            setPlayerActivity(activityRes.data);
            setGamePopularity(popularityRes.data);
            setTopPlayers(topPlayersRes.data);
            setUntaggedSummary(untaggedRes.data);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Detect brand-new signups across poll cycles → toast + chime + desktop notification
    const { requestPermission: requestSignupNotifications } = useNewSignupAlerts(users);

    // Single-key admin shortcuts (`?` opens help overlay)
    const jumpTo = (section, tab) => {
        setCurrentSection(section);
        if (tab) setCurrentTab(tab);
    };
    const { helpOpen: shortcutsHelpOpen, setHelpOpen: setShortcutsHelpOpen } = useAdminShortcuts({
        h: () => { window.scrollTo({ top: 0, behavior: 'smooth' }); jumpTo('players', 'users'); },
        m: () => jumpTo('players', 'users'),     // Messages → users tab where every name is clickable
        t: () => { jumpTo('reports', 'reports'); if (!financialReport) fetchReports(); },
        p: () => jumpTo('players', 'users'),
        r: () => { jumpTo('reports', 'reports'); if (!financialReport) fetchReports(); },
        c: () => jumpTo('casino', 'casino-sync'),
        s: () => jumpTo('settings', 'settings'),
        n: () => jumpTo('players', 'users'),     // 'N' for new player view
    });

    // Listen for the global "open receipts tab" event fired by the floating
    // green receipts badge. Switches the admin to Reports → Reports sub-tab
    // so the SquareReceiptsQueueCard mounts and the pendingOpenId in
    // SquareReceiptsContext can actually open the receipt dialog. Without
    // this hook, clicking the badge from Players/Promotions/Lottery/etc.
    // silently did nothing because no QueueCard was rendered.
    useEffect(() => {
        const handler = () => { jumpTo('reports', 'reports'); };
        window.addEventListener('betohalics:open-receipts-tab', handler);
        return () => window.removeEventListener('betohalics:open-receipts-tab', handler);
    }, []);

    // Lightweight 30s poll for the users list so new signups surface even if
    // the admin hasn't refreshed. Keeps the New (48h) chip and the alert
    // chime in lockstep with reality.
    useEffect(() => {
        let cancelled = false;
        const tick = async () => {
            if (document.hidden) return;
            try {
                const { data } = await axios.get(`${API}/admin/users`, { withCredentials: true });
                if (!cancelled && Array.isArray(data)) {
                    // Only setState when the payload actually changed — kills
                    // the re-render cascade that was flickering child tables.
                    setUsers((prev) => setIfChanged(prev, data));
                }
            } catch { /* silently ignore */ }
        };
        const id = setInterval(tick, 30_000);
        return () => { cancelled = true; clearInterval(id); };
    }, []);

    // Poll lottery feed alerts every 60s so badges update dashboard-wide
    useEffect(() => {
        let cancelled = false;
        const pollAlerts = async () => {
            try {
                const { data } = await axios.get(`${API}/lottery/admin/feed/alerts`);
                if (!cancelled) setLotteryAlertCount(data.count || 0);
            } catch (e) { /* ignore */ }
        };
        pollAlerts();
        const id = setInterval(pollAlerts, 60_000);
        return () => { cancelled = true; clearInterval(id); };
    }, []);

    // Tab-level alert + chime when NEW pending cashout requests arrive
    const lastCashoutCountRef = useRef(null);
    useEffect(() => {
        const count = cashoutRequests?.length || 0;
        const prev = lastCashoutCountRef.current;
        // Push 'info' yellow signal whenever there is anything pending approval
        if (count > 0) {
            setTabSignal('new-cashouts', 'info', count);
        } else {
            setTabSignal('new-cashouts', null);
        }
        // Chime ONLY when count actually grew (and not on first load)
        if (prev != null && count > prev && !isSupportSoundMuted()) {
            playCashoutChime();
        }
        lastCashoutCountRef.current = count;
    }, [cashoutRequests]);

    // Lightweight 30s poll JUST for the cashout-requests endpoint so admins
    // get a tab-bar alert + chime even if they haven't refreshed the page.
    useEffect(() => {
        let cancelled = false;
        const tick = async () => {
            if (document.hidden) return;
            try {
                const { data } = await axios.get(`${API}/admin/cashout-requests`, { withCredentials: true });
                if (!cancelled) setCashoutRequests((prev) => setIfChanged(prev, data || []));
            } catch { /* silently ignore */ }
        };
        const id = setInterval(tick, 30_000);
        return () => { cancelled = true; clearInterval(id); };
    }, []);

    // Clear the cashout signal when leaving the admin dashboard
    useEffect(() => () => setTabSignal('new-cashouts', null), []);

    // Focused refresh for the cashouts list (used after play-back / approve / reject)
    const fetchCashoutRequests = async () => {
        try {
            const { data } = await axios.get(`${API}/admin/cashout-requests`, { withCredentials: true });
            setCashoutRequests(data || []);
        } catch { /* silently ignore */ }
    };

    // Real-time admin awareness: when a player self-serves a play-back (or any
    // play-back happens), surface a toast + soft chime so the floor stays alive.
    const seenPlayBacksRef = useRef(new Set());
    const playBackWarnedRef = useRef(false);
    useEffect(() => {
        let cancelled = false;
        let firstPass = true;
        const pollPlayBacks = async () => {
            try {
                const { data } = await axios.get(`${API}/admin/recent-play-backs?since_seconds=120`, { withCredentials: true });
                if (cancelled) return;
                const rows = data?.rows || [];
                // First pass: just seed the seen set — don't toast on history.
                if (firstPass) {
                    rows.forEach((r) => seenPlayBacksRef.current.add(r.id));
                    firstPass = false;
                    return;
                }
                rows.forEach((r) => {
                    if (seenPlayBacksRef.current.has(r.id)) return;
                    seenPlayBacksRef.current.add(r.id);
                    const amt = `$${Number(r.amount || 0).toFixed(2)}`;
                    const partialTag = r.partial ? ' (partial)' : '';
                    if (r.self_serve) {
                        toast(`${r.user_name} just played back ${amt}${partialTag}`, {
                            description: 'Player kept the credits in play instead of cashing out.',
                            duration: 8000,
                        });
                        playCashoutChime();
                    } else {
                        toast(`${amt} played back for ${r.user_name}${partialTag}`, { duration: 5000 });
                    }
                });
                // Trim the set if it grows large (keep last 200 ids)
                if (seenPlayBacksRef.current.size > 200) {
                    const arr = Array.from(seenPlayBacksRef.current);
                    seenPlayBacksRef.current = new Set(arr.slice(-100));
                }
            } catch (e) {
                if (!playBackWarnedRef.current) {
                    playBackWarnedRef.current = true;
                    console.warn('[admin] /recent-play-backs poll failed — realtime chime disabled this session:', e.message);
                }
            }
        };
        pollPlayBacks();
        const id = setInterval(pollPlayBacks, 20_000);
        return () => { cancelled = true; clearInterval(id); };
    }, []);

    // Poll support chat unread every 4s so the Settings/Support badges stay fresh
    useEffect(() => {
        let cancelled = false;
        const pollSupport = async () => {
            try {
                const { data } = await axios.get(`${API}/admin/support/unread-count`);
                if (cancelled) return;
                setSupportUnreadCount(data.total_unread || 0);

                // Pop a live toast when there's a NEW unread message we haven't seen yet.
                const latest = data.latest_unread;
                const seen = supportLastSeenRef.current;
                if (latest && seen.initialized) {
                    const isNewer =
                        latest.last_message_at !== seen.at ||
                        latest.id !== seen.convoId;
                    if (isNewer) {
                        const who = latest.user_name || latest.user_email || 'A customer';
                        const preview = latest.preview || '(image attachment)';
                        toast.message(`💬 New support message from ${who}`, {
                            description: preview,
                            action: {
                                label: 'Open',
                                onClick: () => {
                                    setCurrentSection('settings');
                                    setCurrentTab('support');
                                },
                            },
                            duration: 8000,
                        });
                        // Soft notification ping (respects mute toggle)
                        if (!isSupportSoundMuted()) {
                            try { playSupportPing(); } catch (_e) { /* ignore */ }
                        }
                        // Optional native browser notification when the tab is hidden / unfocused
                        if (typeof document !== 'undefined' && document.hidden &&
                            'Notification' in window && Notification.permission === 'granted') {
                            try {
                                new Notification(`New support message from ${who}`, {
                                    body: preview, icon: '/favicon.ico', tag: 'support-' + latest.id,
                                });
                            } catch (_e) { /* ignore */ }
                        }
                    }
                }
                supportLastSeenRef.current = {
                    initialized: true,
                    at: latest ? latest.last_message_at : null,
                    convoId: latest ? latest.id : null,
                };
            } catch (e) { /* ignore */ }
        };
        pollSupport();
        // 30s — was 4s, which was hammering the API + causing constant
        // parent re-renders that flickered every tab table on production.
        const id = setInterval(pollSupport, 30_000);
        return () => { cancelled = true; clearInterval(id); };
    }, []);

    // Poll MajikBonus reconciliation gap so the Reports sidebar badge stays
    // truthful across logins. Auto-refresh on the custom event fired by the
    // reconciliation panel after any save.
    useEffect(() => {
        let cancelled = false;
        const fetchGap = async () => {
            try {
                const { data } = await axios.get(`${API}/admin/majik/reconcile-summary`);
                if (cancelled) return;
                setMajikReconGap(data.has_vendor_totals ? Math.max(0, Number(data.net_gap) || 0) : 0);
            } catch { /* silent */ }
        };
        fetchGap();
        const id = setInterval(fetchGap, 5 * 60_000); // every 5 min
        const onUpdate = () => fetchGap();
        window.addEventListener('majik-recon-updated', onUpdate);
        return () => { cancelled = true; clearInterval(id); window.removeEventListener('majik-recon-updated', onUpdate); };
    }, []);

    const handleCreditOperation = async () => {
        if (!selectedUser || !creditAmount) return;
        const amt = parseFloat(creditAmount);
        const action = creditOperation === 'deposit' ? 'load' : 'redeem';
        if (!creditMethod) {
            toast.error(action === 'load'
                ? 'Pick how the customer paid you'
                : 'Pick how the customer is being paid');
            return;
        }
        setConfirmAction({
            type: action,
            message: `Are you sure you want to ${action} ${amt.toFixed(2)} credits ${action === 'load' ? 'to' : 'from'} ${selectedUser.name || selectedUser.email}?`,
            onConfirm: async () => {
                setConfirmActionBusy(true);
                setCreditLoading(true);
                try {
                    await axios.post(`${API}/admin/credits`, {
                        user_id: selectedUser.id,
                        amount: amt,
                        operation: creditOperation,
                        is_comp: creditOperation === 'deposit' && creditIsComp && !creditIsLoan,
                        is_loan: creditOperation === 'deposit' && creditIsLoan,
                        ...(creditOperation === 'deposit'
                            ? { payment_method: creditMethod }
                            : { payout_method: creditMethod }),
                    }, { withCredentials: true });
                    await fetchData();
                    setCreditDialogOpen(false);
                    setCreditAmount('');
                    setCreditMethod('');
                    setCreditIsComp(false);
                    setCreditIsLoan(false);
                    setSelectedUser(null);
                    toast.success(`${amt.toFixed(2)} credits ${action === 'load' ? 'loaded to' : 'redeemed from'} ${selectedUser.name || selectedUser.email}`);
                } catch (error) {
                    toast.error(error?.response?.data?.detail || 'Credit operation failed');
                } finally {
                    setCreditLoading(false);
                    setConfirmActionBusy(false);
                    setConfirmDialogOpen(false);
                    setConfirmAction(null);
                }
            }
        });
        setConfirmDialogOpen(true);
    };

    const handleStatusChange = async (newStatus) => {
        if (!selectedUser) return;
        try {
            await axios.post(`${API}/admin/user-status`, {
                user_id: selectedUser.id,
                status: newStatus
            }, { withCredentials: true });
            await fetchData();
            setStatusDialogOpen(false);
            setSelectedUser(null);
        } catch (error) {
            console.error('Status change failed:', error);
        }
    };

    const handleGrantFreeSpins = async () => {
        if (!selectedUser || !freeSpinsAmount) return;
        try {
            await axios.post(`${API}/admin/free-spins?user_id=${selectedUser.id}&amount=${freeSpinsAmount}`, {}, { withCredentials: true });
            await fetchData();
            setFreeSpinsDialogOpen(false);
            setSelectedUser(null);
        } catch (error) {
            console.error('Free spins grant failed:', error);
        }
    };

    const handleCashoutAction = async (transactionId, action) => {
        const actionLabel = action === 'approve' ? 'approve' : 'reject';
        setConfirmAction({
            type: actionLabel,
            message: `Are you sure you want to ${actionLabel} this cashout request?`,
            targetId: transactionId,
            onConfirm: async () => {
                setConfirmActionBusy(true);
                // Optimistic yank — row disappears from the queue immediately
                // so the admin feels the action is confirmed.
                const snapshot = cashoutRequests;
                setCashoutRequests((prev) => prev.filter((t) => t.id !== transactionId));
                // Close the confirm modal right away — the 5s undo window
                // gives the safety net; no need to trap the admin in the modal.
                setConfirmActionBusy(false);
                setConfirmDialogOpen(false);
                setConfirmAction(null);

                // 5-second undo window — the actual /cashout-action POST is
                // deferred inside the timeout so tapping "Undo" cancels
                // BEFORE we ever hit the server. Safer than posting-then-
                // reverting, since we don't have a true undo endpoint.
                let cancelled = false;
                let toastId;

                const commit = async () => {
                    if (cancelled) return;
                    try {
                        await axios.post(`${API}/admin/cashout-action`, {
                            transaction_id: transactionId,
                            action: action,
                        }, { withCredentials: true });
                        await fetchCashoutRequests();
                        toast.success(`Cashout ${actionLabel}d`, {
                            id: toastId,
                            duration: 3000,
                        });
                    } catch (error) {
                        setCashoutRequests(snapshot);
                        toast.error(
                            error?.response?.data?.detail || `Cashout ${actionLabel} failed`,
                            { id: toastId }
                        );
                    }
                };

                const undo = () => {
                    cancelled = true;
                    setCashoutRequests(snapshot);
                    toast.dismiss(toastId);
                    toast.info(`${actionLabel === 'approve' ? 'Approval' : 'Rejection'} cancelled`, { duration: 2500 });
                };

                toastId = toast(
                    `${actionLabel === 'approve' ? 'Approving' : 'Rejecting'} in 5s…`,
                    {
                        duration: 5000,
                        action: {
                            label: 'UNDO',
                            onClick: undo,
                        },
                        description: 'Tap UNDO if this was a mistake',
                    }
                );

                setTimeout(commit, 5000);
            }
        });
        setConfirmDialogOpen(true);
    };

    const handleSettingsUpdate = async (category, key, value) => {
        setSettingsLoading(true);
        try {
            const newSettings = { ...settings };
            if (!newSettings[category]) newSettings[category] = {};
            newSettings[category][key] = value;
            
            await axios.post(`${API}/admin/settings`, newSettings, { withCredentials: true });
            setSettings(newSettings);
        } catch (error) {
            console.error('Settings update failed:', error);
        } finally {
            setSettingsLoading(false);
        }
    };

    const handleJackpotUpdate = async (newAmount) => {
        try {
            await axios.post(`${API}/admin/jackpot?amount=${newAmount}`, {}, { withCredentials: true });
            await fetchData();
        } catch (error) {
            console.error('Jackpot update failed:', error);
        }
    };

    // Navigate to a specific section + sub-tab from anywhere (digest modal, activity log, etc.)
    const handleAdminNavigate = ({ section, tab, search }) => {
        if (section) setCurrentSection(section);
        if (tab) {
            setCurrentTab(tab);
            if (tab === 'lottery' && !lotteryAdminSettings) fetchLotteryData();
            if (tab === 'reports' && !financialReport) fetchReports();
            if (tab === 'freeplay' && !promoReport) fetchPromoData();
        }
        if (search !== undefined) setSearchTerm(search);
    };

    // Listen for global cross-tab navigation events (e.g., Reports → "Open Dice
    // tab" button dispatches this to jump to Dice → Revenue).
    useEffect(() => {
        const h = (e) => handleAdminNavigate(e.detail || {});
        window.addEventListener('betohalics:admin-navigate', h);
        return () => window.removeEventListener('betohalics:admin-navigate', h);
    }, []);

    const handleCreatePromo = async () => {
        if (!newPromo.code.trim() || !newPromo.amount) return;
        setPromoLoading(true);
        try {
            const payload = { ...newPromo, code: newPromo.code.toUpperCase().trim() };
            if (!payload.expires_at) delete payload.expires_at;
            await axios.post(`${API}/admin/promo-codes`, payload, { withCredentials: true });
            await fetchData();
            setPromoDialogOpen(false);
            setNewPromo({ code: '', bonus_type: 'credits', amount: 0, max_uses: 0, min_vip_tier: 0, min_deposit: 0, expires_at: '', description: '' });
        } catch (error) {
            console.error('Create promo failed:', error?.response?.data?.detail || error);
        } finally {
            setPromoLoading(false);
        }
    };

    const handleTogglePromo = async (code, currentActive) => {
        try {
            await axios.put(`${API}/admin/promo-codes/${code}?is_active=${!currentActive}`, {}, { withCredentials: true });
            await fetchData();
        } catch (error) {
            console.error('Toggle promo failed:', error);
        }
    };

    const handleDeletePromo = async (code) => {
        if (!window.confirm(`Delete promo code ${code}?`)) return;
        try {
            await axios.delete(`${API}/admin/promo-codes/${code}`, { withCredentials: true });
            await fetchData();
        } catch (error) {
            console.error('Delete promo failed:', error);
        }
    };

    const handleProcessCashback = async () => {
        setCashbackProcessing(true);
        setCashbackResult(null);
        try {
            const { data } = await axios.post(`${API}/admin/process-cashback`, {}, { withCredentials: true });
            setCashbackResult(data);
            await fetchData();
        } catch (error) {
            setCashbackResult({ error: error?.response?.data?.detail || 'Failed to process cashback' });
        } finally {
            setCashbackProcessing(false);
        }
    };

    const handleCreateDist = async () => {
        if (!newDist.email || !newDist.password || !newDist.name) return;
        setDistActionLoading(true);
        try {
            await axios.post(`${API}/admin/distributors`, newDist, { withCredentials: true });
            setCreateDistOpen(false);
            setNewDist({ email: '', password: '', name: '', credit_rate: 0.80 });
            await fetchData();
        } catch (error) {
            alert(error?.response?.data?.detail || 'Failed to create distributor');
        } finally {
            setDistActionLoading(false);
        }
    };

    const handleDistTransfer = async () => {
        if (!selectedDist || !distTransferAmount) return;
        setDistActionLoading(true);
        try {
            await axios.post(`${API}/admin/distributors/transfer`, {
                recipient_id: selectedDist.id, amount: parseFloat(distTransferAmount)
            }, { withCredentials: true });
            setDistTransferOpen(false);
            setDistTransferAmount('');
            setSelectedDist(null);
            await fetchData();
        } catch (error) {
            alert(error?.response?.data?.detail || 'Transfer failed');
        } finally {
            setDistActionLoading(false);
        }
    };

    const handleResetPassword = async () => {
        if (!selectedUser || !newPassword || newPassword.length < 6) {
            toast.error('Password must be at least 6 characters');
            return;
        }
        setResetPwLoading(true);
        try {
            const { data } = await axios.post(`${API}/admin/reset-password`, {
                user_id: selectedUser.id,
                new_password: newPassword,
            }, { withCredentials: true });
            toast.success(data.message);
            setResetPwDialogOpen(false);
            setNewPassword('');
            setSelectedUser(null);
        } catch (error) {
            toast.error(error?.response?.data?.detail || 'Failed to reset password');
        } finally {
            setResetPwLoading(false);
        }
    };



    const fetchReports = async ({ silent = false, startDate = null, endDate = null } = {}) => {
        if (!silent) setReportsLoading(true);
        try {
            const dateQs = startDate && endDate
                ? `?start_date=${startDate}&end_date=${endDate}`
                : '';
            const [finRes, betsRes, lottoRes, promoRes] = await Promise.all([
                axios.get(`${API}/admin/reports/financial${dateQs}`, { withCredentials: true }),
                axios.get(`${API}/admin/reports/bets`, { withCredentials: true }),
                axios.get(`${API}/lottery/admin/tickets`, { withCredentials: true }),
                axios.get(`${API}/admin/reports/promotions`, { withCredentials: true }),
            ]);
            setFinancialReport(finRes.data);
            setAllBetsReport(betsRes.data.bets || []);
            setLotteryTickets(lottoRes.data || []);
            setPromoReport(promoRes.data);
        } catch (e) {
            if (!silent) toast.error('Failed to load reports');
        } finally {
            if (!silent) setReportsLoading(false);
        }
    };


    const fetchLotteryData = async () => {
        try {
            const [ticketsRes, drawsRes, settingsRes] = await Promise.all([
                axios.get(`${API}/lottery/admin/tickets`, { withCredentials: true }),
                axios.get(`${API}/lottery/draws`, { withCredentials: true }),
                axios.get(`${API}/lottery/admin/settings`, { withCredentials: true }),
            ]);
            setLotteryTickets(ticketsRes.data);
            setLotteryDraws(drawsRes.data);
            setLotteryAdminSettings(settingsRes.data);
            setEditRules(settingsRes.data?.rules || '');
        } catch { }
    };


    const fetchPromoData = async () => {
        try {
            const [histRes, reportRes, tplRes] = await Promise.all([
                axios.get(`${API}/admin/promotions/history`, { withCredentials: true }),
                axios.get(`${API}/admin/reports/promotions`, { withCredentials: true }),
                axios.get(`${API}/admin/promo-templates`, { withCredentials: true }).catch(() => ({ data: [] })),
            ]);
            setPromoHistory(histRes.data);
            setPromoReport(reportRes.data);
            setPromoTemplates(tplRes.data);
        } catch {}
    };

    const fetchPromoTemplates = async () => {
        try {
            const { data } = await axios.get(`${API}/admin/promo-templates`, { withCredentials: true });
            setPromoTemplates(data);
        } catch {}
    };

    const handleGivePromo = async (targetIds) => {
        const amt = parseFloat(promoAmount);
        if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
        setPromoLoading(true);
        try {
            const payload = { amount: amt, reason: promoReason || 'Promotional credits' };
            if (targetIds && targetIds.length > 0) payload.user_ids = targetIds;
            const { data } = await axios.post(`${API}/admin/promotions/give`, payload, { withCredentials: true });
            toast.success(data.message);
            setPromoAmount('');
            setPromoReason('');
            setPromoSelectedUserIds([]);
            setPromoUserSearch('');
            fetchPromoData();
            fetchData();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed');
        }
        setPromoLoading(false);
    };

    const handleCreateDraw = async () => {
        if (drawNumbers.some(n => n === null)) {
            toast.error('Enter all winning numbers');
            return;
        }
        setDrawLoading(true);
        try {
            const { data } = await axios.post(`${API}/lottery/admin/draw`, {
                state: drawState,
                pick_type: drawPickType,
                winning_numbers: drawNumbers,
                draw_date: drawDate,
                drawing_time: drawTime,
            }, { withCredentials: true });
            toast.success(`${data.message} — ${data.winners} winner(s), ${data.total_payout.toFixed(2)} credits paid`);
            setDrawNumbers(Array(drawPickType).fill(null));
            fetchLotteryData();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to create draw');
        }
        setDrawLoading(false);
    };

    const openCreditDialog = (user, operation) => {
        setSelectedUser(user);
        setCreditOperation(operation);
        setCreditMethod('');
        setCreditIsComp(false);
        setCreditIsLoan(false);
        setCreditAmount('');
        setCreditDialogOpen(true);
    };

    const filteredUsers = users.filter(u => {
        const tokens = searchTerm.toLowerCase().trim().split(/\s+/).filter(Boolean);
        if (!tokens.length) return true;
        const hay = `${u.name || ''} ${u.email || ''} ${u.phone || ''} ${u.ambassador_email || ''} ${u.ambassador_code || ''}`.toLowerCase();
        return tokens.every(t => hay.includes(t));
    });

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'active':
                return <span className="px-2 py-1 rounded-full text-xs font-bold bg-[#39FF14]/20 text-[#39FF14]">Active</span>;
            case 'suspended':
                return <span className="px-2 py-1 rounded-full text-xs font-bold bg-yellow-500/20 text-yellow-500">Suspended</span>;
            case 'banned':
                return <span className="px-2 py-1 rounded-full text-xs font-bold bg-[#FF3B30]/20 text-[#FF3B30]">Banned</span>;
            default:
                return <span className="px-2 py-1 rounded-full text-xs font-bold bg-white/10 text-[#A0AAB2]">{status}</span>;
        }
    };

    if (user?.role !== 'admin') {
        return (
            <div className="min-h-screen bg-[#050505] flex items-center justify-center">
                <div className="text-center">
                    <Shield className="w-16 h-16 text-[#FF3B30] mx-auto mb-4" />
                    <h1 className="text-2xl font-bold text-white mb-2">Access Denied</h1>
                    <p className="text-[#A0AAB2]">You don't have permission to view this page.</p>
                </div>
            </div>
        );
    }

    return (
        <MessagePlayerProvider>
        <PlayerDrilldownProvider>
        <StaffAlertsProvider>
        <SquareReceiptsProvider>
        <LoanOwedProvider>
        <div className="a11y-font admin-readable min-h-screen bg-[#050505]">
            <SupportMessagePopup onOpenSupport={(convoId) => {
                setCurrentSection('settings');
                setCurrentTab('support');
                if (convoId) setTimeout(() => window.dispatchEvent(new CustomEvent('admin:open-support-convo', { detail: convoId })), 400);
            }} />
            {/* Admin Back Office Header */}
            <div className="bg-[#0a0a0a] border-b border-white/10 px-4 py-3">
                <div className="max-w-7xl mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Shield className="w-6 h-6 text-[#FFD700]" />
                        <h1 className="text-xl font-black text-[#FFD700]">BETohalics</h1>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-[#FFD700]/20 text-[#FFD700] font-bold">ADMIN</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <LiveCtClock />
                        <ForgotPasswordInbox onOpenUser={(uid) => {
                            // Mirror HomeDashboard's "jump to Users" pattern
                            setCurrentSection?.('players');
                            setCurrentTab?.('users');
                        }} />
                        <ActivePlayersNowWidget />
                        <PlatformCreditsTile />
                        <StaffAlertsBell />
                        <span className="text-sm text-[#A0AAB2] hidden sm:block">{user?.email}</span>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => { await logout(); navigate('/login'); }}
                            className="border-white/20 text-[#A0AAB2] hover:text-white text-xs"
                            data-testid="admin-logout-btn"
                        >
                            Logout
                        </Button>
                    </div>
                </div>
            </div>
            <CasinoAgentLowBanner />
            
            <div className="max-w-7xl mx-auto px-4 py-8">
                {/* Top-of-dashboard banners — promo-budget burn rate sits next
                    to the Majik low-balance alert. Self-contained, polls every
                    minute, hides itself when nothing's wrong. */}
                <CreditBurnBanner />

                {/* Header */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-black text-white mb-2">
                            Admin <span className="text-[#FFD700]">Back Office</span>
                        </h1>
                        <p className="text-[#A0AAB2]">Complete control over your gaming platform</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Popover>
                            <PopoverTrigger asChild>
                                <button
                                    type="button"
                                    title="Alerts & sound settings"
                                    className="w-10 h-10 rounded-lg border border-white/20 text-[#A0AAB2] hover:text-white hover:bg-white/5 flex items-center justify-center transition-colors relative"
                                    data-testid="admin-quick-settings-btn"
                                >
                                    <Settings className="w-4 h-4" />
                                    {(notifPermission === 'granted' || !soundMuted) && (
                                        <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-[#39FF14]" />
                                    )}
                                </button>
                            </PopoverTrigger>
                            <PopoverContent className="bg-[#0a0a0a] border-white/10 w-72 p-2" align="end">
                                <p className="px-2 py-1 text-[10px] uppercase tracking-widest text-[#A0AAB2] font-bold">Quick Settings</p>
                                <button
                                    type="button"
                                    onClick={async () => {
                                        if (typeof Notification === 'undefined') { toast.error('Browser notifications not supported'); return; }
                                        if (Notification.permission === 'granted') { toast.message('Desktop alerts already on'); return; }
                                        if (Notification.permission === 'denied') { toast.error('Notifications blocked in browser settings'); return; }
                                        requestSignupNotifications();
                                        const id = setInterval(() => {
                                            setNotifPermission(Notification.permission);
                                            if (Notification.permission !== 'default') {
                                                clearInterval(id);
                                                if (Notification.permission === 'granted') toast.success('Desktop signup alerts: ON');
                                            }
                                        }, 500);
                                        setTimeout(() => clearInterval(id), 8000);
                                    }}
                                    className="w-full flex items-center gap-2 px-2 py-2 rounded-md hover:bg-white/5 text-left"
                                    data-testid="quick-settings-notif"
                                >
                                    {notifPermission === 'granted' ? <Bell className="w-4 h-4 text-[#39FF14]" /> : <BellOff className="w-4 h-4 text-[#A0AAB2]" />}
                                    <div className="flex-1">
                                        <p className="text-xs font-bold text-white">Desktop signup alerts</p>
                                        <p className="text-[10px] text-[#A0AAB2]">
                                            {notifPermission === 'granted' ? 'ON' : notifPermission === 'denied' ? 'Blocked in browser' : 'Off · click to enable'}
                                        </p>
                                    </div>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        const newState = !soundMuted;
                                        setSoundMuted(newState);
                                        setSupportSoundMuted(newState);
                                        if (!newState) {
                                            try { playSupportPing(); } catch (_e) { /* ignore */ }
                                            toast.success('Support chat sound: ON');
                                        } else {
                                            toast.message('Support chat sound: muted');
                                        }
                                    }}
                                    className="w-full flex items-center gap-2 px-2 py-2 rounded-md hover:bg-white/5 text-left"
                                    data-testid="quick-settings-sound"
                                >
                                    {soundMuted ? <VolumeX className="w-4 h-4 text-[#FF3B30]" /> : <Volume2 className="w-4 h-4 text-[#39FF14]" />}
                                    <div className="flex-1">
                                        <p className="text-xs font-bold text-white">Support chat sound</p>
                                        <p className="text-[10px] text-[#A0AAB2]">{soundMuted ? 'MUTED · click to unmute' : 'ON · click to mute'}</p>
                                    </div>
                                </button>
                                <button
                                    type="button"
                                    onClick={fetchData}
                                    className="w-full flex items-center gap-2 px-2 py-2 rounded-md hover:bg-white/5 text-left"
                                    data-testid="quick-settings-refresh"
                                >
                                    <RefreshCw className="w-4 h-4 text-[#00E1FF]" />
                                    <div className="flex-1">
                                        <p className="text-xs font-bold text-white">Refresh data</p>
                                        <p className="text-[10px] text-[#A0AAB2]">Pull latest players + reports now</p>
                                    </div>
                                </button>
                            </PopoverContent>
                        </Popover>
                    </div>
                </div>

                {currentSection === 'players' && (
                    <PaybackAlertStrip onOpen={() => jumpToTab({ section: 'reports', tab: 'payback' })} />
                )}

                {/* Streamlined Home Screen — Pending Approvals · Quick Add Credits · Cashout Requests */}
                <HomeDashboard
                    isHome={currentSection === 'players'}
                    users={users}
                    onOpenCredit={openCreditDialog}
                    onOpenFull={(u) => {
                        try { setSelectedUser?.(u); } catch { /* ignore */ }
                        setCurrentSection('players');
                        setCurrentTab('users');
                        setTimeout(() => {
                            const el = document.getElementById(`user-row-${u?.id}`) || document.querySelector('[data-testid="users-admin-tab"]');
                            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }, 350);
                    }}
                    onDrilldown={(key) => {
                        if (key === 'floor-live') {
                            setCurrentSection('reports');
                            setCurrentTab('floor-live');
                            return;
                        }
                        if (key === 'money-in') {
                            setCurrentSection('players');
                            setCurrentTab('paypal-loads');
                            return;
                        }
                        if (key === 'money-out') {
                            setCurrentSection('players');
                            setCurrentTab('cashouts');
                            setTimeout(() => {
                                const el = document.getElementById('cashouts-tab-root');
                                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }, 350);
                            return;
                        }
                        if (key === 'owed') {
                            setCurrentSection('players');
                            setCurrentTab('cashouts');
                            setTimeout(() => {
                                const el = document.getElementById('cashouts-tab-root');
                                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }, 350);
                            return;
                        }
                        if (key === 'agent-score') {
                            setCurrentSection('casino');
                            setCurrentTab('casino-sync');
                            setTimeout(() => {
                                const el = document.querySelector('[data-testid="casino-sync-admin"]');
                                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }, 350);
                            return;
                        }
                        // today-net → financial reports
                        setCurrentSection('reports');
                        setCurrentTab('reports');
                        if (!financialReport) fetchReports();
                    }}
                />

                {/* Main Tabs */}
                <SinceLastLoginDigest onNavigate={handleAdminNavigate} />
                <Tabs value={currentTab} onValueChange={(v) => {
                    // Preserve scrollY across Radix Tabs value change. Radix
                    // will focus the newly-active TabsTrigger + swap the
                    // TabsContent in the DOM, both of which can trigger a
                    // browser auto-scroll on mobile. We lock scrollY across
                    // 2 rAF ticks so the viewport can't jump.
                    const y = window.scrollY;
                    setCurrentTab(v);
                    if (v === 'lottery' && !lotteryAdminSettings) fetchLotteryData();
                    if (v === 'reports' && !financialReport) fetchReports();
                    if (v === 'freeplay' && !promoReport) fetchPromoData();
                    requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                            if (Math.abs(window.scrollY - y) > 4) {
                                window.scrollTo({ top: y, behavior: 'auto' });
                            }
                        });
                    });
                }} className="space-y-4">
                    {/* Universal Command Palette — type any tab name to jump to it */}
                    <div className="relative" data-testid="admin-command-palette">
                        <div className="relative">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#A0AAB2]" />
                            <input
                                type="text"
                                value={paletteQuery}
                                onChange={(e) => { setPaletteQuery(e.target.value); setPaletteOpen(true); }}
                                onFocus={() => paletteQuery && setPaletteOpen(true)}
                                onBlur={() => setTimeout(() => setPaletteOpen(false), 150)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && paletteMatches.length > 0) {
                                        jumpToTab(paletteMatches[0]);
                                    } else if (e.key === 'Escape') {
                                        setPaletteQuery(''); setPaletteOpen(false);
                                    }
                                }}
                                placeholder="Quick find — type any tab, e.g., 'ambassador', 'audit', 'place ticket'…"
                                className="w-full pl-10 pr-3 h-11 bg-[#0a0a0a] border border-white/15 rounded-xl text-white text-sm placeholder:text-[#A0AAB2] focus:outline-none focus:border-[#FFD700]/50"
                                data-testid="admin-command-input"
                                autoComplete="off"
                            />
                            {paletteQuery && (
                                <button
                                    type="button"
                                    onClick={() => { setPaletteQuery(''); setPaletteOpen(false); }}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-[#A0AAB2] hover:text-white p-1"
                                    aria-label="Clear"
                                    data-testid="admin-command-clear"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                        {paletteOpen && paletteMatches.length > 0 && (
                            <div
                                className="absolute z-30 mt-1 w-full bg-[#0e0e0e] border border-white/15 rounded-xl shadow-2xl overflow-hidden"
                                data-testid="admin-command-results"
                            >
                                {paletteMatches.map((r, idx) => (
                                    <button
                                        key={`${r.section}-${r.tab}`}
                                        type="button"
                                        onMouseDown={(e) => { e.preventDefault(); jumpToTab(r); }}
                                        className="w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-white/5 border-b border-white/5 last:border-0"
                                        data-testid={`admin-command-result-${idx}`}
                                    >
                                        <div className="flex items-center gap-2 min-w-0">
                                            <r.icon className="w-4 h-4 text-[#FFD700] shrink-0" />
                                            <div className="min-w-0">
                                                <p className="text-sm text-white font-bold truncate">{r.label}</p>
                                                <p className="text-[10px] text-[#A0AAB2]">
                                                    {r.sectionLabel}
                                                    {r.advanced && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-[#7C3AED]/20 text-[#A988FF] text-[9px] font-bold">ADV</span>}
                                                </p>
                                            </div>
                                        </div>
                                        <span className="text-[9px] text-[#666] uppercase font-bold">jump →</span>
                                    </button>
                                ))}
                            </div>
                        )}
                        {paletteOpen && paletteQuery && paletteMatches.length === 0 && (
                            <div
                                className="absolute z-30 mt-1 w-full bg-[#0e0e0e] border border-white/15 rounded-xl px-3 py-3 text-xs text-[#A0AAB2]"
                                data-testid="admin-command-empty"
                            >
                                No tabs match &quot;{paletteQuery}&quot;.
                            </div>
                        )}
                    </div>

                    {/* Top section nav (Players / Promotions / Reports / Casino / Settings) */}
                    <div className="flex flex-wrap gap-2 p-2 bg-[#0a0a0a] border border-[#333] rounded-2xl" data-testid="admin-section-nav">
                        {ADMIN_SECTIONS.map(s => {
                            // Per-section badge (count + color)
                            let badge = 0;
                            let badgeColor = '#FF007F';
                            let badgeTitle = '';
                            if (s.id === 'players') {
                                const pending = analytics.pending_cashouts || 0;
                                if (pending > 0) {
                                    badge = pending;
                                    badgeColor = '#FF3B30';
                                    badgeTitle = `${pending} pending cashout${pending === 1 ? '' : 's'}`;
                                } else if ((untaggedSummary.loads_count || 0) > 0) {
                                    // Passive nudge — surface untagged dollar volume so admin
                                    // can clean up the by-method breakdown over time.
                                    badge = `$${Math.round(untaggedSummary.loads_total || 0)}`;
                                    badgeColor = '#FF8C00';
                                    badgeTitle = `${untaggedSummary.loads_count} untagged load${untaggedSummary.loads_count === 1 ? '' : 's'} ($${(untaggedSummary.loads_total || 0).toFixed(2)}) in the last 7 days — click Players → Users to tag`;
                                }
                            } else if (s.id === 'promotions') {
                                badge = analytics.promo_fires_recent || 0;
                                badgeColor = '#FFD700';
                                badgeTitle = `${badge} promo fire${badge === 1 ? '' : 's'} in the last hour`;
                            } else if (s.id === 'reports') {
                                if (majikReconGap && majikReconGap >= 100) {
                                    badge = `$${Math.round(majikReconGap / 1000)}K`;
                                    badgeColor = '#F97316';
                                    badgeTitle = `Casino ledger under-count vs vendor: $${majikReconGap.toLocaleString()} — reconcile in Reports → MajikBonus`;
                                } else if (lotteryAlertCount > 0) {
                                    badge = lotteryAlertCount;
                                    badgeColor = '#FF3B30';
                                    badgeTitle = `${badge} overdue lottery draw${badge === 1 ? '' : 's'} — needs attention`;
                                } else {
                                    badge = analytics.unreconciled_snapshots || 0;
                                    badgeColor = '#FF8C00';
                                    badgeTitle = `${badge} unreconciled snapshot${badge === 1 ? '' : 's'} (last 7 days)`;
                                }
                            } else if (s.id === 'settings') {
                                if (supportUnreadCount > 0) {
                                    badge = supportUnreadCount;
                                    badgeColor = '#FF3B30';
                                    badgeTitle = `${badge} unread support message${badge === 1 ? '' : 's'}`;
                                } else {
                                    badge = analytics.recent_activity_count || 0;
                                    badgeColor = '#FF007F';
                                    badgeTitle = `${badge} new admin action${badge === 1 ? '' : 's'} in the last hour`;
                                }
                            }
                            return (
                                <button
                                    key={s.id}
                                    type="button"
                                    onClick={(e) => {
                                        // Preserve scroll position across the re-render.
                                        // Bug reported 2026-07-23: on mobile, clicking a
                                        // section button was making the page "jump" —
                                        // Radix re-mounts the TabsList (different sub-tabs
                                        // per section) and the browser was auto-scrolling
                                        // the newly-focused primitive into view. We blur
                                        // the button so no post-click focus scroll fires,
                                        // and lock the scrollY to whatever it was on click.
                                        const y = window.scrollY;
                                        try { e.currentTarget.blur(); } catch { /* ignore */ }
                                        setCurrentSection(s.id);
                                        // Auto-switch to first sub-tab in the new section
                                        const first = s.tabs[0]?.value;
                                        if (first) {
                                            setCurrentTab(first);
                                            if (first === 'lottery' && !lotteryAdminSettings) fetchLotteryData();
                                            if (first === 'reports' && !financialReport) fetchReports();
                                            if (first === 'freeplay' && !promoReport) fetchPromoData();
                                        }
                                        // Two rAFs — one for the state flush + one for
                                        // Radix's internal focus tick.
                                        requestAnimationFrame(() => {
                                            requestAnimationFrame(() => {
                                                if (Math.abs(window.scrollY - y) > 4) {
                                                    window.scrollTo({ top: y, behavior: 'auto' });
                                                }
                                            });
                                        });
                                    }}
                                    className={`flex-1 sm:flex-none min-h-[48px] px-5 py-3 rounded-xl text-sm font-bold uppercase tracking-wider transition-colors flex items-center gap-2 relative ${
                                        currentSection === s.id
                                            ? 'bg-[#FFD700] text-black'
                                            : 'text-[#A0AAB2] hover:text-white hover:bg-white/5'
                                    }`}
                                    data-testid={`admin-section-${s.id}`}
                                >
                                    <s.icon className="w-4 h-4" />
                                    {s.label}
                                    {(typeof badge === 'string' ? badge.length > 0 : badge > 0) && (
                                        <span
                                            className={`ml-0.5 inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full text-[11px] font-mono font-bold ${
                                                currentSection === s.id ? 'bg-black' : ''
                                            }`}
                                            style={{
                                                backgroundColor: currentSection === s.id ? undefined : badgeColor,
                                                color: currentSection === s.id ? badgeColor : '#fff',
                                            }}
                                            data-testid={`admin-section-${s.id}-badge`}
                                            title={badgeTitle}
                                        >
                                            {typeof badge === 'string' ? badge : (badge > 99 ? '99+' : badge)}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    {/* Sub-tabs for the active section. Power-user "Advanced" tabs
                        (Audit, Analytics, Staff, Slots Sync, Cashback, Referrals,
                        Promo Perf, etc.) are hidden until the toggle is pressed. */}
                    {(() => {
                        const activeSection = ADMIN_SECTIONS.find(s => s.id === currentSection);
                        const primary = activeSection?.tabs || [];
                        const advanced = activeSection?.advancedTabs || [];
                        const visibleTabs = showAdvanced ? [...primary, ...advanced] : primary;
                        const hasAdvanced = advanced.length > 0;
                        return (
                            <TabsList className="bg-[#121212] border border-[#333] flex flex-wrap gap-2 p-2 h-auto rounded-xl">
                                {visibleTabs.map(t => (
                                    <TabsTrigger
                                        key={t.value}
                                        value={t.value}
                                        className="data-[state=active]:bg-[#FFD700] data-[state=active]:text-black text-sm font-bold whitespace-nowrap shrink-0 min-h-[40px] px-4"
                                        data-testid={`tab-${t.value}`}
                                    >
                                        <t.icon className="w-4 h-4 mr-1 hidden sm:inline-block" />
                                        {t.label}
                                        {t.value === 'cashouts' && cashoutRequests.length > 0 && (
                                            <span className="ml-1 px-1.5 py-0.5 rounded-full bg-[#FF3B30] text-white text-[10px]">{cashoutRequests.length}</span>
                                        )}
                                        {t.value === 'lottery' && lotteryAlertCount > 0 && (
                                            <span
                                                className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF3B30] text-white text-[10px] font-bold animate-pulse"
                                                data-testid="tab-lottery-alert-badge"
                                                title={`${lotteryAlertCount} overdue lottery draw${lotteryAlertCount === 1 ? '' : 's'}`}
                                            >
                                                {lotteryAlertCount > 99 ? '99+' : lotteryAlertCount}
                                            </span>
                                        )}
                                        {t.value === 'support' && supportUnreadCount > 0 && (
                                            <span
                                                className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF3B30] text-white text-[10px] font-bold animate-pulse"
                                                data-testid="tab-support-unread-badge"
                                                title={`${supportUnreadCount} unread support message${supportUnreadCount === 1 ? '' : 's'}`}
                                            >
                                                {supportUnreadCount > 99 ? '99+' : supportUnreadCount}
                                            </span>
                                        )}
                                    </TabsTrigger>
                                ))}
                                {hasAdvanced && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowAdvanced(v => {
                                                const next = !v;
                                                // If collapsing while currently on an advanced tab,
                                                // bounce back to the first primary tab so the panel
                                                // doesn't render an "invisible" selected state.
                                                if (!next) {
                                                    const onAdv = advanced.some(at => at.value === currentTab);
                                                    if (onAdv && primary[0]) setCurrentTab(primary[0].value);
                                                }
                                                return next;
                                            });
                                        }}
                                        className={`min-h-[40px] px-3 rounded-md text-xs font-bold uppercase tracking-wider transition border ${showAdvanced
                                            ? 'bg-[#7C3AED]/15 border-[#7C3AED]/50 text-[#A988FF]'
                                            : 'bg-transparent border-white/10 text-[#A0AAB2] hover:text-white hover:border-white/30'
                                            }`}
                                        title={showAdvanced ? 'Hide advanced tabs' : 'Show advanced tabs'}
                                        data-testid="admin-tabs-advanced-toggle"
                                    >
                                        {showAdvanced ? '▾ Hide Advanced' : `▸ Advanced (${advanced.length})`}
                                    </button>
                                )}
                            </TabsList>
                        );
                    })()}

                    {/* Users Tab */}
                    <TabsContent value="users">
                        <UsersAdminTab
                            searchTerm={searchTerm}
                            setSearchTerm={setSearchTerm}
                            loading={loading}
                            filteredUsers={filteredUsers}
                            getStatusBadge={getStatusBadge}
                            openCreditDialog={openCreditDialog}
                            setSelectedUser={setSelectedUser}
                            setFreeSpinsDialogOpen={setFreeSpinsDialogOpen}
                            setResetPwDialogOpen={setResetPwDialogOpen}
                            setStatusDialogOpen={setStatusDialogOpen}
                            fetchData={fetchData}
                        />

                        {/* Customer Contacts (collapsed by default — click to open) */}
                        <div className="mt-6">
                            <Button
                                onClick={() => setShowContacts((v) => !v)}
                                variant="outline"
                                className="w-full justify-between bg-[#0a0a0a] border-white/10 text-white hover:bg-white/5 hover:text-[#FFD700]"
                                data-testid="toggle-customer-contacts-btn"
                            >
                                <span className="flex items-center gap-2 font-semibold">
                                    <Users className="w-4 h-4 text-[#FFD700]" />
                                    Customer Contacts Directory
                                </span>
                                <span className="text-xs text-[#A0AAB2]">
                                    {showContacts ? 'Hide ▴' : 'Open ▾'}
                                </span>
                            </Button>
                            {showContacts && (
                                <div className="mt-3" data-testid="customer-contacts-section">
                                    <ContactsAdminPanel />
                                </div>
                            )}
                        </div>
                    </TabsContent>

                    {/* Transactions Tab */}
                    <TabsContent value="transactions">
                        <TransactionsAdminTab loading={loading} transactions={transactions} formatDate={formatDate} />
                    </TabsContent>

                    {/* PayPal Loads Tab — one-click list of who loaded via PayPal */}
                    <TabsContent value="paypal-loads">
                        <PayPalLoadsAdminTab />
                    </TabsContent>

                    {/* Cashouts Tab */}
                    <TabsContent value="cashouts">
                        <CashoutsAdminTab
                            loading={loading}
                            cashoutRequests={cashoutRequests}
                            handleCashoutAction={handleCashoutAction}
                            formatDate={formatDate}
                            fetchCashoutRequests={fetchCashoutRequests}
                        />
                    </TabsContent>

                    {/* Promos Tab */}
                    <TabsContent value="promos">
                        <PromoCodesAdminTab
                            setPromoDialogOpen={setPromoDialogOpen}
                            promoCodes={promoCodes}
                            handleTogglePromo={handleTogglePromo}
                            handleDeletePromo={handleDeletePromo}
                            promoRedemptions={promoRedemptions}
                        />
                    </TabsContent>

                    {/* Daily Special — admin-curated featured sportsbook bet */}
                    <TabsContent value="daily-special">
                        <DailySpecialAdminPanel />
                    </TabsContent>

                    {/* Series Pack — playoff series prediction */}
                    <TabsContent value="series-pack">
                        <SeriesPackAdminPanel />
                    </TabsContent>

                    {/* Lucky 5 — tiered sports lottery pack */}
                    <TabsContent value="lucky-pack">
                        <LuckyPackAdminPanel />
                    </TabsContent>

                    {/* Bonus Activity — live feed of every free play / bonus grant */}
                    <TabsContent value="bonus-feed">
                        <BonusFeedAdminPanel />
                    </TabsContent>

                    {/* Ambassadors — non-player promo codes */}
                    <TabsContent value="ambassadors">
                        <AmbassadorAdminPanel />
                    </TabsContent>

                    {/* Wheel the Names — ad-hoc promo wheel */}
                    <TabsContent value="wheels">
                        <WheelsAdminPanel role="admin" />
                    </TabsContent>

                    {/* Cashback Tab */}
                    <TabsContent value="cashback">
                        <CashbackAdminTab
                            cashbackStats={cashbackStats}
                            cashbackProcessing={cashbackProcessing}
                            cashbackResult={cashbackResult}
                            handleProcessCashback={handleProcessCashback}
                            settings={settings}
                            handleSettingsUpdate={handleSettingsUpdate}
                        />
                    </TabsContent>

                    {/* Referrals Tab */}
                    <TabsContent value="referrals">
                        <ReferralsAdminTab
                            referralStats={referralStats}
                            settings={settings}
                            handleSettingsUpdate={handleSettingsUpdate}
                        />
                    </TabsContent>

                    {/* Hierarchy Tab */}
                    <TabsContent value="hierarchy">
                        <HierarchyAdminTab users={users} fetchData={fetchData} />
                    </TabsContent>

                    {/* Analytics Charts Tab */}
                    <TabsContent value="analytics-charts">
                        <AnalyticsChartsAdminTab
                            revenueChart={revenueChart}
                            playerActivity={playerActivity}
                            topPlayers={topPlayers}
                        />
                    </TabsContent>


                    {/* Settings Tab */}
                    <TabsContent value="settings">
                        <SettingsAdminTab
                            settings={settings}
                            handleSettingsUpdate={handleSettingsUpdate}
                            onNavigate={handleAdminNavigate}
                        />
                        <div className="mt-6">
                            <SupportHoursAdminPanel />
                        </div>
                        <div className="mt-6">
                            <PokerHappyHourPanel />
                        </div>
                        <div className="mt-6">
                            <PokerBuyInBoostPanel />
                        </div>
                        <div className="mt-6">
                            <PokerAdminPanel />
                        </div>
                    </TabsContent>

                    {/* Staff Tab — admin creates distributors & operators */}
                    <TabsContent value="staff">
                        <StaffAdminTab />
                    </TabsContent>

                    {/* Cashiers Tab — admin creates cashiers directly */}
                    <TabsContent value="cashiers">
                        <AdminCashiersTab />
                    </TabsContent>

                    {/* Help Center editor */}
                    <TabsContent value="help">
                        <HelpAdminTab />
                    </TabsContent>

                    {/* Role Access — operator/cashier dashboard tab visibility */}
                    <TabsContent value="role-access">
                        <RoleAccessAdminTab />
                    </TabsContent>

                    {/* Support Chat Tab — live customer inbox */}
                    <TabsContent value="support">
                        <SupportAdminTab />
                    </TabsContent>

                    {/* Kiosks Tab — cash-loading terminals */}
                    <TabsContent value="kiosks">
                        <KioskAdminPanel />
                    </TabsContent>

                    {/* Audit Logs Tab */}
                    <TabsContent value="audit">
                        <AuditAdminTab loading={loading} auditLogs={auditLogs} formatDate={formatDate} />
                    </TabsContent>

                    {/* Weekly Settlement Tab — admin reconciliation */}
                    <TabsContent value="settlement">
                        <WeeklySettlementPanel />
                    </TabsContent>

                    {/* Reports Tab */}
                    <TabsContent value="reports">
                        <ReportsAdminTab
                            financialReport={financialReport}
                            allBetsReport={allBetsReport}
                            lotteryTickets={lotteryTickets}
                            promoReport={promoReport}
                            reportsLoading={reportsLoading}
                            fetchReports={fetchReports}
                        />
                        <div className="mt-6">
                            <MajikReconciliationPanel />
                        </div>
                        <div className="mt-6">
                            <MajikErrorsPanel />
                        </div>
                    </TabsContent>

                    {/* Sports hub — all sportsbook admin in one place */}
                    <TabsContent value="sportsbook">
                        <SportsAdminTab />
                    </TabsContent>

                    <TabsContent value="payback">
                        <PaybackReportPanel />
                    </TabsContent>

                    <TabsContent value="daily-profit">
                        <DailyProfitPanel />
                    </TabsContent>

                    {/* Games Performance sub-tab (Reports → Games) */}
                    <TabsContent value="games-performance">
                        <GamesPerformancePanel />
                    </TabsContent>

                    {/* Player P&L Log Tab (troubleshooting) */}
                    <TabsContent value="player-pnl">
                        <PlayerPnLTab />
                    </TabsContent>

                    <TabsContent value="bonus-analytics">
                        <BonusAnalyticsPanel />
                    </TabsContent>

                    {/* Place Ticket — fast daily flow */}
                    <TabsContent value="place-ticket">
                        <AdminLotteryHelperPanel />
                    </TabsContent>

                    <TabsContent value="flagged-tickets">
                        <AdminFlaggedTicketsTab />
                    </TabsContent>

                    {/* Draws & Settings — power-user lottery admin */}
                    <TabsContent value="lottery">
                        <div className="space-y-4">
                            <BulkDrawBackfillPanel />
                            <LotteryAdminTab
                            drawState={drawState} setDrawState={setDrawState}
                            drawPickType={drawPickType} setDrawPickType={setDrawPickType}
                            drawNumbers={drawNumbers} setDrawNumbers={setDrawNumbers}
                            drawDate={drawDate} setDrawDate={setDrawDate}
                            drawTime={drawTime} setDrawTime={setDrawTime}
                            drawLoading={drawLoading}
                            handleCreateDraw={handleCreateDraw}
                            lotteryDraws={lotteryDraws}
                            lotteryTickets={lotteryTickets}
                            lotteryAdminSettings={lotteryAdminSettings}
                            setLotteryAdminSettings={setLotteryAdminSettings}
                            editRules={editRules}
                            setEditRules={setEditRules}
                            fetchLotteryData={fetchLotteryData}
                        />
                        </div>
                    </TabsContent>

                    {/* Free Play / Promotions Tab */}
                    <TabsContent value="freeplay">
                        <FreePlayTab
                            users={users}
                            promoSelectedUserIds={promoSelectedUserIds}
                            setPromoSelectedUserIds={setPromoSelectedUserIds}
                            promoUserSearch={promoUserSearch}
                            setPromoUserSearch={setPromoUserSearch}
                            promoAmount={promoAmount}
                            setPromoAmount={setPromoAmount}
                            promoReason={promoReason}
                            setPromoReason={setPromoReason}
                            promoLoading={promoLoading}
                            promoReport={promoReport}
                            promoHistory={promoHistory}
                            fetchPromoData={fetchPromoData}
                            handleGivePromo={handleGivePromo}
                            setConfirmAction={setConfirmAction}
                            setConfirmDialogOpen={setConfirmDialogOpen}
                            promoTemplates={promoTemplates}
                            fetchPromoTemplates={fetchPromoTemplates}
                        />
                    </TabsContent>

                    <TabsContent value="promo-perf">
                        <PromoPerformanceTab />
                    </TabsContent>

                    <TabsContent value="announcements">
                        <AnnouncementsAdminTab />
                    </TabsContent>

                    <TabsContent value="keno-configs">
                        <KenoConfigsAdminTab />
                    </TabsContent>
                    <TabsContent value="keno-reports">
                        <KenoReportsAdminTab />
                    </TabsContent>
                    <TabsContent value="keno-round-lookup">
                        <KenoRoundLookupTab />
                    </TabsContent>
                    <TabsContent value="game-players">
                        <GamePlayersTab />
                    </TabsContent>
                    <TabsContent value="floor-live">
                        <FloorLiveAdminTab />
                    </TabsContent>
                    <TabsContent value="lucky-ducky-profiles">
                        <LuckyDuckyProfilesAdminTab />
                    </TabsContent>
                    <TabsContent value="lucky-ducky-reports">
                        <LuckyDuckyReportsAdminTab />
                    </TabsContent>
                    <TabsContent value="lucky-ducky-bots">
                        <BotStudioAdminTab />
                    </TabsContent>
                    <TabsContent value="lucky-ducky-qa">
                        <LuckyDuckyQAAdminTab />
                    </TabsContent>
                    <TabsContent value="game-availability">
                        <GameAvailabilityPanel />
                        <MalfunctionGuardPanel />
                    </TabsContent>

                    <TabsContent value="game-rtp">
                        <GameRtpAdminTab />
                    </TabsContent>

                    <TabsContent value="buffalo-bucks">
                        <BuffaloBucksReportsAdminTab />
                    </TabsContent>

                    <TabsContent value="bof">
                        <div className="space-y-6">
                            <BofReportsAdminTab />
                            <BofSettingsAdminTab />
                            <BofJackpotsAdminTab />
                        </div>
                    </TabsContent>

                    <TabsContent value="thunder-cash">
                        <div className="space-y-6">
                            <ThunderCashReportsAdminTab />
                            <ThunderCashSettingsAdminTab />
                        </div>
                    </TabsContent>

                    <TabsContent value="panda-paradise">
                        <div className="space-y-6">
                            <PandaParadiseSettingsAdminTab />
                        </div>
                    </TabsContent>

                    <TabsContent value="deep-sea-furtune">
                        <DeepSeaFurtuneAdminTab />
                    </TabsContent>

                    <TabsContent value="outlaws-loot">
                        <div className="space-y-6">
                            <OutlawsLootReportsAdminTab />
                        </div>
                    </TabsContent>

                    <TabsContent value="jackpots">
                        <div className="mb-6">
                            <GameAvailabilityPanel />
                        </div>
                        <JackpotsAdminTab />
                        <div className="mt-6">
                            <CasinoJackpotConfigPanel />
                        </div>
                        {/* Blackjack financials — mirrored here so the report
                            is discoverable from Slots section too, not
                            only under Reports > Reports sub-tab. */}
                        <div className="mt-6">
                            <SportsbookReportCard />
                        </div>
                        <div className="mt-6">
                            <BlackjackReportCard />
                        </div>
                        {/* 3-Card Poker admin — config + report */}
                        <div className="mt-6">
                            <ThreeCardPokerConfigPanel />
                        </div>
                        <div className="mt-6">
                            <ThreeCardPokerReportCard />
                        </div>
                        {/* Craps admin — config + report */}
                        <div className="mt-6">
                            <CrapsConfigPanel />
                        </div>
                        <div className="mt-6">
                            <CrapsReportCard />
                        </div>
                        {/* Roulette admin — config + report */}
                        <div className="mt-6">
                            <RouletteConfigPanel />
                        </div>
                        <div className="mt-6">
                            <RouletteReportCard />
                        </div>
                    </TabsContent>

                    <TabsContent value="casino-sync">
                        <CasinoSyncAdminTab />
                    </TabsContent>

                    <TabsContent value="dice-revenue">
                        <HouseRakeTile />
                    </TabsContent>
                    <TabsContent value="dice-settings">
                        <DiceSettingsPanel />
                    </TabsContent>
                    <TabsContent value="dice-simulator">
                        <StreetDiceSimPanel />
                    </TabsContent>
                    <TabsContent value="tonk-settings">
                        <TonkSettingsPanel />
                    </TabsContent>
                    <TabsContent value="tonk-tables">
                        <TonkTablesPanel />
                    </TabsContent>
                    <TabsContent value="tonk-audit">
                        <TonkAuditPanel />
                    </TabsContent>
                </Tabs>
            </div>

            {/* Credit Dialog */}
            <Dialog open={creditDialogOpen} onOpenChange={setCreditDialogOpen}>
                <DialogContent className="bg-[#121212] border-white/10">
                    <DialogHeader>
                        <DialogTitle className="text-white">{creditOperation === 'deposit' ? 'Add Credits' : 'Remove Credits'}</DialogTitle>
                        <DialogDescription className="text-[#A0AAB2]">
                            {selectedUser?.name} - Current: {selectedUser?.credits?.toFixed(2) || '0.00'}
                        </DialogDescription>
                        {selectedUser?.last_load_at && (
                            <div className="mt-2 px-3 py-2 rounded-lg bg-[#FF007F]/10 border border-[#FF007F]/20 text-xs flex items-center gap-2 flex-wrap" data-testid="last-loaded-hint">
                                <span className="text-[#A0AAB2]">Last loaded:</span>
                                <span className="font-mono text-[#FF007F] font-bold">${(selectedUser.last_load_amount || 0).toFixed(2)}</span>
                                {selectedUser.last_load_method && (
                                    <span className="text-[#A0AAB2]">via <span className="text-white font-semibold">{selectedUser.last_load_method}</span></span>
                                )}
                                <span className="text-[#A0AAB2]">on {new Date(selectedUser.last_load_at).toLocaleString('en-US', {
                                    timeZone: 'America/Chicago',
                                    month: 'short', day: 'numeric',
                                    hour: 'numeric', minute: '2-digit',
                                })} CT</span>
                            </div>
                        )}
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        {/* Quick amount buttons — cumulative (click multiple to add up) */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <p className="text-[10px] uppercase font-bold text-[#A0AAB2] tracking-wide">Quick amount <span className="text-[#666] normal-case font-normal">(tap to add)</span></p>
                                {parseFloat(creditAmount) > 0 && (
                                    <button
                                        type="button"
                                        onClick={() => setCreditAmount('')}
                                        className="text-[10px] text-[#FF3B30] hover:text-[#FF3B30]/80 font-bold uppercase tracking-wide"
                                        data-testid="credit-clear-amount"
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>
                            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="credit-quick-amounts">
                                {[5, 10, 20, 50, 100, 500].map((amt) => (
                                    <button
                                        key={amt}
                                        type="button"
                                        onClick={() => {
                                            const cur = parseFloat(creditAmount) || 0;
                                            setCreditAmount(String(Math.round((cur + amt) * 100) / 100));
                                        }}
                                        className="px-2 py-2 rounded-lg text-sm font-mono font-bold border bg-[#0a0a0a] text-white border-white/10 hover:border-[#FFD700]/40 hover:bg-white/5 active:scale-95 transition-all"
                                        data-testid={`credit-quick-${amt}`}
                                    >
                                        +${amt}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <p className="text-[10px] uppercase font-bold text-[#A0AAB2] tracking-wide mb-2">
                                Total amount {parseFloat(creditAmount) > 0 && (
                                    <span className={`ml-2 font-mono ${creditOperation === 'deposit' ? 'text-[#39FF14]' : 'text-[#FF3B30]'}`}>
                                        ${parseFloat(creditAmount).toFixed(2)}
                                    </span>
                                )}
                            </p>
                            <Input type="number" value={creditAmount} onChange={(e) => setCreditAmount(e.target.value)}
                                placeholder="Type a custom amount or use the buttons above"
                                className="bg-[#0a0a0a] border-white/10 text-white" data-testid="credit-amount-input" />
                        </div>
                        {/* Payment / Payout Method chips — REQUIRED */}
                        <div>
                            <p className="text-[10px] uppercase font-bold text-[#A0AAB2] tracking-wide mb-2">
                                {creditOperation === 'deposit'
                                    ? 'How did the customer pay you?'
                                    : 'How is the customer being paid?'}
                                <span className="ml-1 text-[#FF3B30]">*</span>
                            </p>
                            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="credit-method-chips">
                                {CREDIT_PAYMENT_METHODS.map((m) => {
                                    const active = creditMethod === m;
                                    return (
                                        <button
                                            key={m}
                                            type="button"
                                            onClick={() => setCreditMethod(m)}
                                            className={`px-2 py-2 rounded-lg text-xs font-bold border transition-all active:scale-95 ${
                                                active
                                                    ? 'bg-[#FFD700] text-black border-[#FFD700]'
                                                    : 'bg-[#0a0a0a] text-white border-white/10 hover:border-[#FFD700]/40 hover:bg-white/5'
                                            }`}
                                            data-testid={`credit-method-${m.toLowerCase().replace(/\s+/g, '-')}`}
                                        >
                                            {m}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Comp / no-cash-taken toggle. Shown only for deposit so we
                        don't confuse the operator on cashouts. When checked,
                        the load is tagged so it shows up in the Credit
                        Reconciliation under "Comp Loads (no cash)" and won't
                        trigger the daily-bonus or referral progression. */}
                    {creditOperation === 'deposit' && (
                        <div className="mt-3 space-y-2">
                            <label className="flex items-center gap-2 cursor-pointer text-xs text-[#A0AAB2]" data-testid="credit-comp-toggle">
                                <input
                                    type="checkbox"
                                    checked={creditIsComp}
                                    onChange={(e) => { setCreditIsComp(e.target.checked); if (e.target.checked) setCreditIsLoan(false); }}
                                    className="w-4 h-4 rounded border-white/20 bg-[#0a0a0a] text-[#A855F7] focus:ring-[#A855F7]"
                                    data-testid="credit-comp-checkbox"
                                />
                                <span>
                                    <span className={`font-bold ${creditIsComp ? 'text-[#A855F7]' : 'text-white'}`}>Comp / no cash taken</span>
                                    <span className="block text-[10px] text-[#666]">Tags as comp — skips daily bonus & referral; tracked separately.</span>
                                </span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer text-xs text-[#A0AAB2]" data-testid="credit-loan-toggle">
                                <input
                                    type="checkbox"
                                    checked={creditIsLoan}
                                    onChange={(e) => { setCreditIsLoan(e.target.checked); if (e.target.checked) setCreditIsComp(false); }}
                                    className="w-4 h-4 rounded border-white/20 bg-[#0a0a0a] text-[#FF6B35] focus:ring-[#FF6B35]"
                                    data-testid="credit-loan-checkbox"
                                />
                                <span>
                                    <span className={`font-bold ${creditIsLoan ? 'text-[#FF6B35]' : 'text-white'}`}>Loan / owed back</span>
                                    <span className="block text-[10px] text-[#666]">Player is fronted credits they owe back — appears in Reports under Loans.</span>
                                </span>
                            </label>
                        </div>
                    )}

                    <DialogFooter>
                        <Button onClick={handleCreditOperation} disabled={creditLoading || !creditAmount || !creditMethod}
                            className={creditOperation === 'deposit' ? 'bg-[#39FF14] text-black' : 'bg-[#FF3B30]'}
                            data-testid="confirm-credit-btn">
                            {creditLoading ? 'Processing...' :
                                creditOperation === 'deposit'
                                    ? (creditIsLoan ? 'Loan Credits' : creditIsComp ? 'Comp Credits' : 'Add Credits')
                                    : 'Remove Credits'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Status Dialog */}
            <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
                <DialogContent className="bg-[#121212] border-white/10">
                    <DialogHeader>
                        <DialogTitle className="text-white">Change User Status</DialogTitle>
                        <DialogDescription className="text-[#A0AAB2]">
                            {selectedUser?.name} - Current: {selectedUser?.status || 'active'}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 py-4">
                        <Button onClick={() => handleStatusChange('active')} className="w-full bg-[#39FF14] text-black" data-testid="status-active">
                            <CheckCircle className="w-4 h-4 mr-2" />Set Active
                        </Button>
                        <Button onClick={() => handleStatusChange('suspended')} variant="outline" className="w-full border-yellow-500 text-yellow-500" data-testid="status-suspended">
                            <AlertTriangle className="w-4 h-4 mr-2" />Suspend
                        </Button>
                        <Button onClick={() => handleStatusChange('banned')} variant="outline" className="w-full border-[#FF3B30] text-[#FF3B30]" data-testid="status-banned">
                            <Ban className="w-4 h-4 mr-2" />Ban
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Reset Password Dialog */}
            <Dialog open={resetPwDialogOpen} onOpenChange={(open) => { setResetPwDialogOpen(open); if (!open) setNewPassword(''); }}>
                <DialogContent className="bg-[#121212] border-white/10">
                    <DialogHeader>
                        <DialogTitle className="text-white">Reset Password</DialogTitle>
                        <DialogDescription className="text-[#A0AAB2]">
                            Set a new password for {selectedUser?.name} ({selectedUser?.email})
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div>
                            <Label className="text-white">New Password</Label>
                            <Input
                                type="text"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                placeholder="Enter new password (min 6 chars)"
                                className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                                data-testid="reset-pw-input"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            onClick={handleResetPassword}
                            disabled={resetPwLoading || newPassword.length < 6}
                            className="bg-[#00BFFF] text-black hover:bg-[#00BFFF]/80"
                            data-testid="confirm-reset-pw-btn"
                        >
                            {resetPwLoading ? 'Resetting...' : <><KeyRound className="w-4 h-4 mr-2" />Reset Password</>}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Confirmation Dialog */}
            <Dialog open={confirmDialogOpen} onOpenChange={(open) => {
                // Guard: never let the user dismiss while an action is running —
                // that used to make the "Yes, Approve" look frozen because the
                // dialog closed under a still-running POST.
                if (open) return;
                if (confirmActionBusy) return;
                setConfirmDialogOpen(false);
                setConfirmAction(null);
            }}>
                <DialogContent className="bg-[#121212] border-white/10">
                    <DialogHeader>
                        <DialogTitle className="text-white">Confirm Action</DialogTitle>
                        <DialogDescription className="text-[#A0AAB2] text-base mt-2">
                            {confirmAction?.message}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="flex gap-2 sm:gap-2 mt-4">
                        <Button
                            variant="outline"
                            onClick={() => { setConfirmDialogOpen(false); setConfirmAction(null); }}
                            disabled={confirmActionBusy}
                            className="border-white/20 text-[#A0AAB2] hover:text-white disabled:opacity-50"
                            data-testid="confirm-cancel-btn"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={() => { if (!confirmActionBusy) confirmAction?.onConfirm?.(); }}
                            disabled={confirmActionBusy}
                            className={`${
                                confirmAction?.type === 'reject' ? 'bg-[#FF3B30] hover:bg-[#FF3B30]/80' :
                                confirmAction?.type === 'redeem' ? 'bg-[#FF9500] hover:bg-[#FF9500]/80' :
                                'bg-[#39FF14] hover:bg-[#39FF14]/80'
                            } text-black font-bold disabled:opacity-70 disabled:cursor-wait`}
                            data-testid="confirm-yes-btn"
                        >
                            {confirmActionBusy ? (
                                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Working…</>
                            ) : (
                                <>Yes, {confirmAction?.type === 'load' ? 'Load Credits' : confirmAction?.type === 'redeem' ? 'Redeem Credits' : confirmAction?.type === 'approve' ? 'Approve' : 'Reject'}</>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Free Spins Dialog */}
            <Dialog open={freeSpinsDialogOpen} onOpenChange={setFreeSpinsDialogOpen}>
                <DialogContent className="bg-[#121212] border-white/10">
                    <DialogHeader>
                        <DialogTitle className="text-white">Grant Free Spins</DialogTitle>
                        <DialogDescription className="text-[#A0AAB2]">
                            {selectedUser?.name} - Current: {selectedUser?.free_spins || 0} free spins
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <Input type="number" value={freeSpinsAmount} onChange={(e) => setFreeSpinsAmount(e.target.value)}
                            placeholder="Number of free spins" className="bg-[#0a0a0a] border-white/10 text-white" data-testid="freespins-amount-input" />
                    </div>
                    <DialogFooter>
                        <Button onClick={handleGrantFreeSpins} className="bg-[#FF007F]" data-testid="confirm-freespins-btn">
                            <Star className="w-4 h-4 mr-2" />Grant Free Spins
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Create Promo Dialog */}
            <Dialog open={promoDialogOpen} onOpenChange={setPromoDialogOpen}>
                <DialogContent className="bg-[#121212] border-white/10 max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="text-white">Create Promo Code</DialogTitle>
                        <DialogDescription className="text-[#A0AAB2]">
                            Create a new promotional bonus code for players
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div>
                            <Label className="text-white">Code</Label>
                            <Input value={newPromo.code} onChange={(e) => setNewPromo({ ...newPromo, code: e.target.value.toUpperCase() })}
                                placeholder="e.g. WELCOME50" className="mt-1 bg-[#0a0a0a] border-white/10 text-white font-mono uppercase"
                                data-testid="new-promo-code" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label className="text-white">Bonus Type</Label>
                                <Select value={newPromo.bonus_type} onValueChange={(v) => setNewPromo({ ...newPromo, bonus_type: v })}>
                                    <SelectTrigger className="mt-1 bg-[#0a0a0a] border-white/10 text-white" data-testid="new-promo-type">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#121212] border-white/10">
                                        <SelectItem value="credits" className="text-white">Credits</SelectItem>
                                        <SelectItem value="percentage" className="text-white">Percentage Bonus</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-white">Amount</Label>
                                <Input type="number" value={newPromo.amount} onChange={(e) => setNewPromo({ ...newPromo, amount: parseFloat(e.target.value) || 0 })}
                                    className="mt-1 bg-[#0a0a0a] border-white/10 text-white" data-testid="new-promo-amount" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label className="text-white">Max Uses (0 = unlimited)</Label>
                                <Input type="number" value={newPromo.max_uses} onChange={(e) => setNewPromo({ ...newPromo, max_uses: parseInt(e.target.value) || 0 })}
                                    className="mt-1 bg-[#0a0a0a] border-white/10 text-white" data-testid="new-promo-max-uses" />
                            </div>
                            <div>
                                <Label className="text-white">Min VIP Tier</Label>
                                <Select value={String(newPromo.min_vip_tier)} onValueChange={(v) => setNewPromo({ ...newPromo, min_vip_tier: parseInt(v) })}>
                                    <SelectTrigger className="mt-1 bg-[#0a0a0a] border-white/10 text-white" data-testid="new-promo-vip">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#121212] border-white/10">
                                        <SelectItem value="0" className="text-white">Any (Bronze+)</SelectItem>
                                        <SelectItem value="1" className="text-white">Silver+</SelectItem>
                                        <SelectItem value="2" className="text-white">Gold+</SelectItem>
                                        <SelectItem value="3" className="text-white">Platinum+</SelectItem>
                                        <SelectItem value="4" className="text-white">Diamond Only</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div>
                            <Label className="text-white">Expiry Date (optional)</Label>
                            <Input type="date" value={newPromo.expires_at} onChange={(e) => setNewPromo({ ...newPromo, expires_at: e.target.value })}
                                className="mt-1 bg-[#0a0a0a] border-white/10 text-white" data-testid="new-promo-expiry" />
                        </div>
                        <div>
                            <Label className="text-white">Description</Label>
                            <Input value={newPromo.description} onChange={(e) => setNewPromo({ ...newPromo, description: e.target.value })}
                                placeholder="e.g. Welcome bonus for new players" className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                                data-testid="new-promo-description" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button onClick={handleCreatePromo} disabled={promoLoading || !newPromo.code.trim() || !newPromo.amount}
                            className="btn-primary" data-testid="confirm-create-promo">
                            {promoLoading ? 'Creating...' : 'Create Promo Code'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Create Distributor Dialog */}
            <Dialog open={createDistOpen} onOpenChange={setCreateDistOpen}>
                <DialogContent className="bg-[#121212] border-white/10">
                    <DialogHeader>
                        <DialogTitle className="text-white">Create Distributor</DialogTitle>
                        <DialogDescription className="text-[#A0AAB2]">Create a new distributor in the credit hierarchy</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div>
                            <Label className="text-white">Name</Label>
                            <Input value={newDist.name} onChange={e => setNewDist({ ...newDist, name: e.target.value })}
                                className="mt-1 bg-[#0a0a0a] border-white/10 text-white" data-testid="new-dist-name" />
                        </div>
                        <div>
                            <Label className="text-white">Email</Label>
                            <Input type="email" value={newDist.email} onChange={e => setNewDist({ ...newDist, email: e.target.value })}
                                className="mt-1 bg-[#0a0a0a] border-white/10 text-white" data-testid="new-dist-email" />
                        </div>
                        <div>
                            <Label className="text-white">Password</Label>
                            <Input type="password" value={newDist.password} onChange={e => setNewDist({ ...newDist, password: e.target.value })}
                                className="mt-1 bg-[#0a0a0a] border-white/10 text-white" data-testid="new-dist-password" />
                        </div>
                        <div>
                            <Label className="text-white">Credit Rate (your sell price per credit, e.g. 0.80 = $0.80)</Label>
                            <Input type="number" step="0.01" value={newDist.credit_rate} onChange={e => setNewDist({ ...newDist, credit_rate: parseFloat(e.target.value) || 0 })}
                                className="mt-1 bg-[#0a0a0a] border-white/10 text-white" data-testid="new-dist-rate" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button onClick={handleCreateDist} disabled={distActionLoading} className="btn-primary" data-testid="confirm-create-dist">
                            {distActionLoading ? 'Creating...' : 'Create Distributor'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Transfer to Distributor Dialog */}
            <Dialog open={distTransferOpen} onOpenChange={setDistTransferOpen}>
                <DialogContent className="bg-[#121212] border-white/10">
                    <DialogHeader>
                        <DialogTitle className="text-white">Load Credits to {selectedDist?.name}</DialogTitle>
                        <DialogDescription className="text-[#A0AAB2]">
                            Distributor balance: {(selectedDist?.credits || 0).toFixed(2)} credits
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <Input type="number" value={distTransferAmount} onChange={e => setDistTransferAmount(e.target.value)}
                            placeholder="Amount" className="bg-[#0a0a0a] border-white/10 text-white font-mono" data-testid="dist-transfer-amount" />
                        <div className="flex gap-2">
                            {[1000, 5000, 10000, 25000].map(v => (
                                <button key={v} onClick={() => setDistTransferAmount(String(v))}
                                    className="px-3 py-1 text-xs rounded bg-white/10 text-[#A0AAB2] hover:bg-white/20">{v.toLocaleString()}</button>
                            ))}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button onClick={handleDistTransfer} disabled={distActionLoading || !distTransferAmount} className="btn-primary" data-testid="confirm-dist-transfer">
                            {distActionLoading ? 'Transferring...' : 'Load Credits'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Keyboard shortcuts help overlay (press `?`) */}
            <Dialog open={shortcutsHelpOpen} onOpenChange={setShortcutsHelpOpen}>
                <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-md" data-testid="shortcuts-help-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-white">Keyboard Shortcuts</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-2 mt-2">
                        {[
                            { k: 'H', label: 'Home (players)' },
                            { k: 'M', label: 'Messages — click any name to chat' },
                            { k: 'T', label: 'Today\'s Report' },
                            { k: 'P', label: 'Players' },
                            { k: 'R', label: 'Reports' },
                            { k: 'C', label: 'Slots' },
                            { k: 'S', label: 'Settings' },
                            { k: 'N', label: 'New signups (48h)' },
                            { k: '?', label: 'Show / hide this menu' },
                            { k: 'Esc', label: 'Close any dialog' },
                        ].map(({ k, label }) => (
                            <div key={k} className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-0">
                                <span className="text-sm text-[#A0AAB2]">{label}</span>
                                <kbd className="px-2 py-1 rounded bg-white/10 border border-white/20 text-white text-xs font-mono font-bold min-w-[2rem] text-center">{k}</kbd>
                            </div>
                        ))}
                    </div>
                    <p className="text-[10px] text-[#A0AAB2] mt-3 text-center">
                        Shortcuts ignore when you're typing in a field.
                    </p>
                </DialogContent>
            </Dialog>
        </div>
        </LoanOwedProvider>
        </SquareReceiptsProvider>
        </StaffAlertsProvider>
        </PlayerDrilldownProvider>
        </MessagePlayerProvider>
    );
};

export default AdminDashboard;
